import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { t } from '@lingui/core/macro';

import CloseIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

import { useComboboxContext } from './ComboboxContext';

export type ComboboxChipProps = ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
  disabled?: boolean;
};

const pickerControlChipSizeClasses = {
  small: 'h-[calc(--spacing(4.25))]',
  medium: 'h-[calc(--spacing(6))]',
  large: 'h-[calc(--spacing(7))]',
};

const closeButtonSizeClasses = {
  small: 'size-3.5 [&_svg]:size-3.5',
  medium: 'size-4 [&_svg]:size-4',
  large: 'size-4.5 [&_svg]:size-4',
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  disabled: disabledProp = false,
  ...props
}: ComboboxChipProps) => {
  const { size, disabled: disabledRoot = false } = useComboboxContext();
  const disabled = disabledProp || disabledRoot;

  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      aria-disabled={disabled}
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1 rounded-xs px-1.5 text-xs font-medium whitespace-nowrap',
        'bg-green-300 text-gray-900',
        'focus-visible:bg-green-400 focus-visible:outline-green-500',
        'has-data-[slot=combobox-chip-remove]:pr-0',
        'aria-disabled:cursor-not-allowed aria-disabled:bg-gray-200! aria-disabled:text-gray-500!',
        pickerControlChipSizeClasses[size],
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          data-slot="combobox-chip-remove"
          className="mr-0.5 -ml-0.5 opacity-50 hover:opacity-100 disabled:pointer-events-none disabled:text-gray-500!"
          render={
            <Button
              className={closeButtonSizeClasses[size]}
              aria-label={t`Remove item`}
              disabled={disabled}
              variant="ghost"
              size="icon-small"
            >
              <CloseIcon className="pointer-events-none" />
            </Button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  );
};

export default ComboboxChip;
